import { useState, useEffect, useCallback } from 'react';
import { api } from '../../services/api';
import { Experience, ApiError, PaginatedResponse, ExperienceQueryParams, Language } from '../../types/api';

interface UseExperiencesState {
  experiences: Experience[];
  loading: boolean;
  error: ApiError | null;
  pagination: {
    page: number;
    limit: number;
    total: number;
    totalPages: number;
  } | null;
}

interface UseExperiencesReturn extends UseExperiencesState {
  fetchExperiences: (params?: { page?: number; limit?: number }) => Promise<void>;
  getExperienceById: (id: string) => Promise<Experience | null>;
  refresh: () => Promise<void>;
}

export const useExperiences = (
  initialParams?: ExperienceQueryParams,
  autoFetch: boolean = true,
  language: Language = 'pt'
): UseExperiencesReturn => {
  const [state, setState] = useState<UseExperiencesState>({
    experiences: [],
    loading: false,
    error: null,
    pagination: null,
  });

  const [queryParams, setQueryParams] = useState<{ page?: number; limit?: number }>(
    initialParams || { page: 1, limit: 20 }
  );

  const fetchExperiences = useCallback(async (params?: ExperienceQueryParams) => {
    setState((prev) => ({ ...prev, loading: true, error: null }));

    try {
      const response: PaginatedResponse<Experience> = await api.experiences.getAll({
        ...(params || queryParams),
        language,
      });
      setState({
        experiences: response.data,
        loading: false,
        error: null,
        pagination: response.pagination,
      });
      if (params) setQueryParams(params);
    } catch (error: any) {
      setState((prev) => ({ ...prev, loading: false, error }));
    }
  }, [language, queryParams]);

  const getExperienceById = useCallback(async (id: string): Promise<Experience | null> => {
    setState((prev) => ({ ...prev, loading: true, error: null }));

    try {
      const experience = await api.experiences.getById(id);
      setState((prev) => ({ ...prev, loading: false }));
      return experience;
    } catch (error) {
      setState((prev) => ({
        ...prev,
        loading: false,
        error: error as ApiError,
      }));
      return null;
    }
  }, []);

  const refresh = useCallback(() => {
    return fetchExperiences(queryParams);
  }, [fetchExperiences, queryParams]);

  useEffect(() => {
    if (autoFetch) {
      fetchExperiences();
    }
  }, [autoFetch, fetchExperiences]);

  return {
    ...state,
    fetchExperiences,
    getExperienceById,
    refresh,
  };
};
